import React from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SegmentedMeter from '../../../../../../components/SegmentedMeter';
import {getBPTitle, getStandardTitle} from '../../../../../../utils/taskHelpers';


const COMPLIANCE_SEGMENTS = 7;
const BEST_PRACTICES_SEGMENTS = 10;

function SeverityIndicator({count, taskType}) {
    const totalSegments = taskType === 'best_practices_check' ? BEST_PRACTICES_SEGMENTS : COMPLIANCE_SEGMENTS;
    const missingCount = Math.min(count, totalSegments);
    const fill = totalSegments - missingCount;

    return (
        <SegmentedMeter filled={fill} total={totalSegments} size="default"/>
    );
}

/**
 * Card component for compliance_check and best_practices_check tasks
 */
export default function ComplianceCheckTaskCard({task}) {
    const standardIds = task.standardIds || [];
    const instructionsByStandard = task.instructionsByStandard || {};
    const isBestPractices = task.taskType === 'best_practices_check';

    // Use appropriate title getter based on task type
    const getTitleForStandard = isBestPractices ? getBPTitle : getStandardTitle;

    return (
        <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <SeverityIndicator count={standardIds.length} taskType={task.taskType}/>
            </Stack>

            {standardIds.length > 0 && (
                <Stack spacing={0.5}>
                    {standardIds.map((standardId) => {
                        const instructions = instructionsByStandard[standardId] || [];
                        return (
                            <Accordion
                                key={standardId}
                                disableGutters
                                elevation={0}
                                sx={{
                                    '&:before': {display: 'none'},
                                    backgroundColor: 'action.hover',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon/>}
                                    sx={{
                                        minHeight: 36,
                                        p: 1,
                                        pl: 1.5,
                                        '& .MuiAccordionSummary-content': {my: 0.5},
                                    }}
                                >
                                    <Typography variant="body2">
                                        <strong>{standardId}</strong>: {getTitleForStandard(standardId)}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{pt: 0, pb: 1}}>
                                    {instructions.length > 0 ? (
                                        <Stack component="ul" spacing={0.5} sx={{pl: 2, my: 0}}>
                                            {instructions.map((instruction, index) => (
                                                <Typography key={index} component="li" variant="body2">
                                                    {instruction}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No specific instructions
                                        </Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Stack>
            )}

            {task.suggestedText && (
                <>
                    <Typography variant="body2" fontWeight="bold">
                        Suggested Text:
                    </Typography>
                    <Typography variant="body1">
                        {task.suggestedText}
                    </Typography>
                </>
            )}
        </Stack>
    );
}

